import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import edit from "../assets/images/icons/edit_b.svg";
import { EndPoints } from "../Constants/endpoints";
import { PregnantFormData } from "../interface/GeneralResponse";
import { useToast } from "../Services/ToastContext";
import { useGetData, usePostData } from "../hooks";
import { GeneralButton, Modal } from "../components";

const pregnantSchema = z.object({
  Pregnant: z.union([z.number().nullable(), z.null()]).optional(),
});

function WeekPregnant() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data, isLoading, refetch } = useGetData<PregnantFormData>(
    EndPoints.GetPregnancy,
  );

  const { mutate, isPending } = usePostData(EndPoints.EditPregnancy);
  const methods = useForm<PregnantFormData>({
    defaultValues: {
      Pregnant: null,
    },
    resolver: zodResolver(pregnantSchema),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = methods;

  const [selectedValue, setSelectedValue] = useState<number | null>(null);

  const location = useLocation();

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };

  useEffect(() => {
    if (data?.Data) {
      reset({
        Pregnant: data?.Data.Pregnant ?? null,
      });
      setSelectedValue(data?.Data.Pregnant ?? null);
    }
  }, [data?.Data, reset]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleShareClick = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const onSubmit = (formData: PregnantFormData) => {
    if (formData.Pregnant === null) {
      return;
    }
    mutate(
      { Pregnant: formData.Pregnant },
      {
        onSuccess: () => {
          showToast(t("pregnant_updated_successfully"), "success");
          refetch();
          closeModal();
        },
      },
    );
  };
  const handleWeekClick = (index: number) => {
    setSelectedValue(index + 1);
    setValue("Pregnant", index + 1);
  };

  return (
    <div>
      <Modal
        header={t("share_profile")}
        isOpen={isModalOpen}
        onClose={closeModal}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
            <p className="text-body-text mb-2 text-lg">
              {t("select_your_week_of_pregnancy")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {Array.from({ length: 40 }, (_, index) => (
                <div
                  key={index + 1}
                  className={`flex cursor-pointer items-center justify-center shadow-sm ${
                    selectedValue === index + 1 ? "bg-primary text-white" : ""
                  } h-9 w-9 rounded-full transition-all hover:bg-primary hover:text-white`}
                  onClick={() => handleWeekClick(index)}
                >
                  <label>
                    <Controller
                      name="Pregnant"
                      control={control}
                      render={({ field }) => (
                        <input type="hidden" {...field} value={index + 1} />
                      )}
                    />
                    {index + 1}
                  </label>
                </div>
              ))}
            </div>
            {errors.Pregnant && (
              <p className="mt-2 text-sm text-error">{t("required")}</p>
            )}
            <div className="mt-5 flex max-w-md gap-2 self-end">
              <GeneralButton
                fontColor="body-text"
                title={t("cancel")}
                color="lite"
                onClick={() => handleBack()}
              />
              <GeneralButton
                type="submit"
                title={t("save_changes")}
                color="blue"
                disabled={isLoading || isPending}
              />
            </div>
          </form>
        </FormProvider>
      </Modal>
      <img
        src={edit}
        alt="Edit"
        className="cursor-pointer"
        onClick={handleShareClick}
      />
    </div>
  );
}

export default WeekPregnant;
